import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { apiRequest, ApiError } from '../lib/api'
import {
  acknowledgeEvents,
  completeSection as completeSectionState,
  loadProgress,
  mergeProgress,
  progressStorageKey,
  recordPosition as recordPositionState,
  saveProgress,
  sectionKey,
  type CloudProgress,
  type CourseProgress,
} from '../lib/progress'
import { COURSE_VERSION } from '../version'
import { useAuth } from './AuthContext'

export type SyncStatus = 'guest' | 'saved' | 'syncing' | 'saved-device' | 'retrying' | 'sign-in'

interface ProgressContextValue {
  progress: CourseProgress
  syncStatus: SyncStatus
  completeSection: (courseId: string, moduleId: string, sectionId: string) => void
  recordPosition: (courseId: string, moduleId: string, sectionId: string) => void
  isSectionComplete: (moduleId: string, sectionId: string) => boolean
  completedForModule: (moduleId: string) => number
  syncNow: () => Promise<void>
}

interface SyncResponse {
  acceptedEventIds: string[]
  revision: number
  progress?: CloudProgress
}

const ProgressContext = createContext<ProgressContextValue | null>(null)

export function ProgressProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth()
  const [progress, setProgress] = useState<CourseProgress>(() => loadProgress())
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(session ? 'syncing' : 'guest')
  const progressRef = useRef(progress)
  const syncingRef = useRef(false)
  const retryRef = useRef<number | null>(null)
  const retryIndex = useRef(0)

  useEffect(() => {
    progressRef.current = progress
    saveProgress(progress)
  }, [progress])

  const scheduleRetry = useCallback(() => {
    if (retryRef.current !== null) return
    const delays = [3_000, 10_000, 30_000]
    const delay = delays[Math.min(retryIndex.current, delays.length - 1)]
    retryIndex.current += 1
    setSyncStatus('retrying')
    retryRef.current = window.setTimeout(() => {
      retryRef.current = null
      window.dispatchEvent(new Event('investment-course:retry-sync'))
    }, delay)
  }, [])

  const syncNow = useCallback(async () => {
    if (!session || syncingRef.current) {
      if (!session) setSyncStatus('guest')
      return
    }
    const snapshot = progressRef.current
    if (!snapshot.pendingEvents.length) {
      setSyncStatus('saved')
      return
    }
    syncingRef.current = true
    setSyncStatus('syncing')
    try {
      const result = await apiRequest<SyncResponse>(
        'sync_progress',
        {
          courseVersion: COURSE_VERSION,
          baseRevision: snapshot.revision,
          events: snapshot.pendingEvents,
        },
        { token: session.token },
      )
      setProgress((current) => acknowledgeEvents(
        current,
        result.data.acceptedEventIds ?? snapshot.pendingEvents.map((event) => event.eventId),
        result.data.revision ?? current.revision,
        result.data.progress,
      ))
      retryIndex.current = 0
      setSyncStatus('saved')
    } catch (error) {
      if (error instanceof ApiError && (error.code === 'UNAUTHORIZED' || error.code === 'SESSION_EXPIRED')) {
        setSyncStatus('sign-in')
      } else {
        setSyncStatus('saved-device')
        scheduleRetry()
      }
    } finally {
      syncingRef.current = false
    }
  }, [scheduleRetry, session])

  useEffect(() => {
    if (!session) {
      setSyncStatus('guest')
      return
    }
    let active = true
    setSyncStatus('syncing')
    void apiRequest<CloudProgress>('get_progress', {}, { token: session.token })
      .then(({ data }) => {
        if (!active) return
        setProgress((current) => {
          const merged = mergeProgress(current, data)
          progressRef.current = merged
          return merged
        })
        window.setTimeout(() => void syncNow(), 0)
      })
      .catch((error) => {
        if (!active) return
        setSyncStatus(error instanceof ApiError && (error.code === 'UNAUTHORIZED' || error.code === 'SESSION_EXPIRED') ? 'sign-in' : 'saved-device')
      })
    return () => {
      active = false
    }
  }, [session?.token])

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === progressStorageKey) setProgress(loadProgress())
    }
    const onOnline = () => void syncNow()
    const onRetry = () => void syncNow()
    window.addEventListener('storage', onStorage)
    window.addEventListener('online', onOnline)
    window.addEventListener('investment-course:retry-sync', onRetry)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('online', onOnline)
      window.removeEventListener('investment-course:retry-sync', onRetry)
      if (retryRef.current !== null) window.clearTimeout(retryRef.current)
    }
  }, [syncNow])

  const commit = useCallback((next: CourseProgress) => {
    progressRef.current = next
    setProgress(next)
    if (session) window.setTimeout(() => void syncNow(), 0)
  }, [session, syncNow])

  const completeSection = useCallback((courseId: string, moduleId: string, sectionId: string) => {
    commit(completeSectionState(progressRef.current, { courseId, moduleId, sectionId }))
  }, [commit])

  const recordPosition = useCallback((courseId: string, moduleId: string, sectionId: string) => {
    commit(recordPositionState(progressRef.current, { courseId, moduleId, sectionId }))
  }, [commit])

  const isSectionComplete = useCallback(
    (moduleId: string, sectionId: string) => Boolean(progress.completedSections[sectionKey(moduleId, sectionId)]),
    [progress.completedSections],
  )

  const completedForModule = useCallback(
    (moduleId: string) => Object.values(progress.completedSections).filter((item) => item.moduleId === moduleId).length,
    [progress.completedSections],
  )

  const value = useMemo<ProgressContextValue>(() => ({
    progress,
    syncStatus,
    completeSection,
    recordPosition,
    isSectionComplete,
    completedForModule,
    syncNow,
  }), [progress, syncStatus, completeSection, recordPosition, isSectionComplete, completedForModule, syncNow])

  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>
}

export function useProgress(): ProgressContextValue {
  const value = useContext(ProgressContext)
  if (!value) throw new Error('useProgress must be used inside ProgressProvider.')
  return value
}
